import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import EscarapelaIcon from '../components/ui/EscarapelaIcon';
import SolDivider from '../components/ui/SolDivider';
import { BADGES_INFO } from '../mockData';
import { avatarPublicUrl } from '../lib/memberHelpers';
import { motion } from 'framer-motion';

export default function PublicProfile() {
  const { id } = useParams();
  const { members, CAPITULOS } = useApp();
  const navigate = useNavigate();

  const member = members.find(m => m.id === id);

  if (!member) return <div className="p-20 text-center font-editorial text-2xl">Miembro no encontrado.</div>;

  const capitulo = CAPITULOS.find(c => c.id === member.capitulo_id);
  const featuredBadges = member.badges?.filter(b => b.featured).slice(0, 3) || [];
  const hasBadges = member.badges && member.badges.length > 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto space-y-8">
      
      <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium">
        <SafeIcon name="ArrowLeft" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Volver al Directorio
      </button>

      <div className="bg-white rounded-[40px] shadow-2xl shadow-stone-200/50 border border-stone-100 overflow-hidden">
        {/* Profile Header Band */}
        <div className="h-48 bg-[#1B2A4E] relative">
          <div className="absolute inset-0 opacity-10 sol-watermark"></div>
          <div className="absolute -bottom-16 left-12">
            <img
              src={
                avatarPublicUrl(member.foto_url) ||
                `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(member.nombre || '?')}`
              }
              alt={member.nombre}
              className="w-40 h-40 rounded-[32px] object-cover border-8 border-white shadow-xl"
            />
          </div>
        </div>

        <div className="pt-20 px-12 pb-12">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6">
            <div>
              <h1 className="text-5xl font-editorial font-bold text-[#1B2A4E] flex flex-wrap items-center gap-4">
                {member.nombre}
                {featuredBadges.length > 0 && (
                  <span className="flex items-center gap-2">
                    {featuredBadges.map(b => {
                      const info = BADGES_INFO[b.id];
                      if (!info) return null;
                      const isAbanderado = b.id === 'abanderado';
                      
                      return (
                        <div 
                          key={b.id} 
                          title={info.name} 
                          className={`flex items-center justify-center rounded-full p-2 shadow-sm border ${isAbanderado ? 'bg-white border-stone-200' : 'bg-stone-50 border-stone-200 text-stone-600'}`}
                        >
                          {isAbanderado ? <EscarapelaIcon className="w-5 h-5" /> : <SafeIcon name={info.icon} className="w-5 h-5" />}
                        </div>
                      );
                    })}
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="bg-[#74ACDF]/10 text-[#74ACDF] px-4 py-1.5 rounded-full label-technical text-xs">
                  {member.rubro}
                </span>
                <div className="flex items-center gap-1.5 text-stone-400 font-medium">
                  <SafeIcon name="MapPin" className="w-4 h-4 text-[#F6B40E]" />
                  {capitulo?.nombre || 'Global'}
                </div>
              </div>
            </div>
            
            {member.whatsapp_visible && (
              <a href={`https://wa.me/${member.whatsapp_number?.replace(/\+/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg hover:scale-105">
                <SafeIcon name="MessageCircle" className="w-6 h-6" /> Contactar por WhatsApp
              </a>
            )}
          </div>

          <SolDivider className="my-10" />

          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#74ACDF]/10 flex items-center justify-center text-[#74ACDF]">
                  <SafeIcon name="Briefcase" className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-editorial font-bold text-[#1B2A4E]">Su Especialidad</h3>
              </div>
              <div className="bg-stone-50 p-8 rounded-3xl border border-stone-100">
                <p className="text-lg text-[#1B2A4E] leading-relaxed italic font-medium">
                  &ldquo;{member.ofrezco}&rdquo;
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F6B40E]/10 flex items-center justify-center text-[#F6B40E]">
                  <SafeIcon name="Search" className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-editorial font-bold text-[#1B2A4E]">Lo que necesita</h3>
              </div>
              <div className="bg-stone-50 p-8 rounded-3xl border border-stone-100">
                <p className="text-[#1B2A4E] leading-relaxed">
                  {member.busco}
                </p>
              </div>
            </div>
          </div>

          {/* Insignias Section */}
          {hasBadges && (
            <div className="mt-12 pt-8 border-t border-stone-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1B2A4E]/5 flex items-center justify-center text-[#1B2A4E]">
                    <SafeIcon name="Award" className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-editorial font-bold text-[#1B2A4E]">Insignias del Miembro</h3>
                    <p className="text-sm text-stone-500 mt-1">Reconocimientos obtenidos en la comunidad.</p>
                  </div>
                </div>
                <Link to="/insignias" className="text-sm font-bold text-[#74ACDF] hover:text-[#1B2A4E] transition-colors label-technical flex items-center gap-1.5">
                  Ver todas <SafeIcon name="ArrowRight" className="w-3.5 h-3.5" />
                </Link>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {member.badges.map(b => {
                  const info = BADGES_INFO[b.id];
                  if (!info) return null;
                  const isAbanderado = b.id === 'abanderado';
                  
                  return (
                    <div key={b.id} className="group relative bg-[#FAF9F6] border border-stone-100 rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-all">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isAbanderado ? 'bg-white border border-stone-200 shadow-sm' : 'bg-white text-stone-500 border border-stone-200'}`}>
                        {isAbanderado ? <EscarapelaIcon className="w-7 h-7" /> : <SafeIcon name={info.icon} className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className="font-bold text-[#1B2A4E] text-sm leading-tight">{info.name}</h4>
                        <p className="text-[10px] text-stone-400 font-medium mt-1 label-technical">Válida hasta {new Date(b.expiresAt).toLocaleDateString('es')}</p>
                      </div>
                      
                      {/* Tooltip on Hover */}
                      <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-[#1B2A4E] text-white text-xs p-3 rounded-lg shadow-xl z-10 text-center pointer-events-none">
                        {info.description}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1B2A4E]"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Referral Card */}
      <div className="bg-gradient-to-br from-[#74ACDF] to-[#1B2A4E] rounded-[40px] p-12 text-white relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full -mr-40 -mt-40 blur-3xl" />
        <div className="max-w-xl relative z-10">
          <h3 className="text-3xl font-editorial font-bold mb-4">¿Querés presentarle a alguien?</h3>
          <p className="text-white/80 text-lg mb-8">
            El Movimiento crece por recomendaciones. Usá este link para invitar a otro emprendedor que deba conocer a {member.nombre}.
          </p>
          <div className="flex p-1 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
            <input 
              readOnly 
              value={`movimiento.com/invitacion/${member.invite_code}`}
              className="bg-transparent border-none text-white px-4 py-3 flex-1 text-sm outline-none"
            />
            <button className="bg-[#F6B40E] text-[#1B2A4E] px-6 py-3 rounded-xl font-bold text-sm hover:bg-white transition-colors">
              Copiar Link
            </button>
          </div>
        </div>
      </div>

    </motion.div>
  );
}